import dayjs from 'dayjs';
import { HealthProviderId } from './HealthProvider';
import { ID } from './Firebase';
import { Patient } from './Patient';
import { UserOffer } from './UserOffer';
import { Pharamcy } from './Pharamcy';

export type OrderState =
    | 'emitted'
    | 'assigned'
    | 'validated'
    | 'approved'
    | 'ready_for_pickup'
    | 'delivering'
    | 'fulfilled'
    | 'cancellation_requested'
    | 'canceled';
export type HpAuthentication = {
    token: string;
    state: 'none' | 'requested' | 'sent' | 'error';
    last_request_timestamp?: Date;
    last_sent_timestamp?: Date;
    last_error?: string;
    last_error_timestamp?: Date;
};

type OrderFeedback = {
    nps?: number;
    comment?: string;
    origin?: string;
    timestamp: Date;
};
export type ViewableConfig = {
    pharmacy_id: string;
    viewable_at: Date;
    is_referrer: boolean;
    distance: number;
};
export type Order = {
    id: ID;
    customer_id: ID;
    short_id: string;
    state: OrderState;
    health_provider: HealthProviderId;
    delivery_zone: string;
    creation_timestamp: Date;
    user_offer?: UserOffer;
    viewable_by: ID[];
    viewable_timeline: ViewableConfig[];
    trip_type?: 'one_way' | 'round_trip';
    assigned_pharmacy_id: string;
    patient: Patient;
    hp_authentication?: HpAuthentication;
    delivery_id?: ID;
    feedback?: OrderFeedback;
};

export const isOTC = (order: Order) => order.health_provider === 'otc';
export const hasOffer = (order: Order) => !!order.user_offer;
export const isPast = (order: Order) => order.state === 'fulfilled';
export const requiresApproval = (order: Order) => {
    const requiresActionList: OrderState[] = ['assigned', 'approved'];
    return (
        (order.state === 'emitted' && !hasOffer(order)) || requiresActionList.includes(order.state)
    );
};
export const requiresAttention = (order: Order) =>
    requiresApproval(order) || order.state === 'cancellation_requested';
export const inEmittedState = (order: Order) => order.state === 'emitted';
export const inValidatingState = (order: Order) => {
    const validatingStatesList: OrderState[] = ['assigned', 'validated'];
    return validatingStatesList.includes(order.state);
};
export const inProcessingState = (order: Order) => {
    const processingStatesList: OrderState[] = ['approved', 'ready_for_pickup', 'delivering'];
    return processingStatesList.includes(order.state);
};

export const isDisclosedOrder = (x: Order): boolean => x.state !== 'emitted';

export const needsHpAuthToken = (order: Order) => {
    const hpsWithAuthentication: HealthProviderId[] = [
        'osde',
        'swiss_medical',
        'asociacion_mutual_sancor_salud',
        'plus_medical',
    ];
    return order.state === 'assigned' && hpsWithAuthentication.includes(order.health_provider);
};

export const isSchedulePending = (order: Order) =>
    !!order.user_offer?.scheduled_stock_datetime &&
    order.user_offer?.scheduled_stock_datetime >= new Date();
export const isScheduled = (order: Order) => !!order.user_offer?.scheduled_stock_datetime;

export const isReferredOrder = (user: Pharamcy, order: Order) => {
    if (!order.viewable_timeline.length) return false;
    return order.viewable_timeline.find((x) => x.pharmacy_id === user.id && x.is_referrer);
};

export const shouldBeShown = (o: Order, userId: String) => {
    if (!o.viewable_timeline.length) {
        return true;
    }

    const viewableConfig = o.viewable_timeline.find((x) => x.pharmacy_id === userId);

    if (!viewableConfig) {
        return false;
    }
    const now = dayjs().add(1, 'millisecond');
    return now.isAfter(viewableConfig.viewable_at);
};

export const findNextViewableAt = (orders: Order[], userId: string): Date | undefined => {
    const emittedOrders = orders.filter(
        (o) => o.state === 'emitted' && !!o.viewable_timeline.length,
    );

    const now = dayjs().subtract(2, 'seconds');
    const viewableDates = emittedOrders
        .flatMap((o) =>
            o.viewable_timeline.filter(
                (t) =>
                    t.pharmacy_id === userId && // is current user
                    dayjs(t.viewable_at).isAfter(now), // Must be future
            ),
        )
        .map((viewableConfig) => viewableConfig.viewable_at)
        .sort((a, b) => dayjs(a).diff(dayjs(b))); // Sort by closest time

    return viewableDates.length ? viewableDates[0] : undefined;
};
